import { useEffect, useRef, useState, useCallback } from 'react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfiguration) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonConfig) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin' | 'signup' | 'use';
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
  clientId?: string;
}

interface GoogleButtonConfig {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string | number;
  locale?: string;
}

interface UseGoogleAuthProps {
  clientId: string;
  onSuccess: (credentialResponse: GoogleCredentialResponse) => void;
  onError?: (error: any) => void;
  autoSelect?: boolean;
}

interface UseGoogleAuthReturn {
  isLoaded: boolean;
  renderButton: (element: HTMLElement, config?: GoogleButtonConfig) => void;
  error: string | null;
}

export const useGoogleAuth = ({
  clientId,
  onSuccess,
  onError,
  autoSelect = false,
}: UseGoogleAuthProps): UseGoogleAuthReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializeAttempted = useRef(false);

  useEffect(() => {
    if (initializeAttempted.current) return;
    
    const initializeGoogleAuth = () => {
      if (!window.google?.accounts?.id) {
        setError('Google Identity Services not loaded');
        return;
      }

      if (!clientId || clientId === 'your-google-client-id.apps.googleusercontent.com') {
        setError('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment variables.');
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: GoogleCredentialResponse) => {
            try {
              onSuccess(response);
            } catch (err) {
              console.error('Error in Google auth callback:', err);
              onError?.(err);
            }
          },
          auto_select: autoSelect,
          cancel_on_tap_outside: false,
          context: 'signin',
        });
        
        setIsLoaded(true);
        setError(null);
      } catch (err) {
        console.error('Error initializing Google Auth:', err);
        setError('Failed to initialize Google authentication');
        onError?.(err);
      }
    };

    // Check if Google script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogleAuth();
      initializeAttempted.current = true;
    } else {
      // Wait for Google script to load
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleAuth();
          initializeAttempted.current = true;
        }
      }, 100);

      // Cleanup interval after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkGoogleLoaded);
        if (!window.google?.accounts?.id) {
          setError('Google Identity Services failed to load');
        }
      }, 10000);

      return () => {
        clearInterval(checkGoogleLoaded);
        clearTimeout(timeout);
      };
    }
  }, [clientId, onSuccess, onError, autoSelect]);

  const renderButton = useCallback((element: HTMLElement, config: GoogleButtonConfig = {}) => {
    if (!isLoaded || !window.google?.accounts?.id) {
      console.warn('Google Auth not loaded yet');
      return;
    }

    try {
      const defaultConfig: GoogleButtonConfig = {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: '100%',
        ...config,
      };

      window.google.accounts.id.renderButton(element, defaultConfig);
    } catch (err) {
      console.error('Error rendering Google button:', err);
      setError('Failed to render Google sign-in button');
      onError?.(err);
    }
  }, [isLoaded, onError]);

  return {
    isLoaded,
    renderButton,
    error,
  };
};

export default useGoogleAuth;